/**
 * @file index.tsx
 *
 * @fileoverview Renders a Horizontal card paragraph type in Cards Slider.
 */

import React, { useContext } from 'react';
import { HorizontalCard as HorizontalCardComponent } from '~/vcl/organisms/04-landingpage/08-horizontal-card';
import Image from '~/jobiqo/components/Image';
import { LinkButton } from '~/vcl/atoms/01-forms/01-button/02-link-button';
import { WYSIWYGText } from '~/vcl/atoms/13-text/02-WYSIWYG';
import Link from '~/jobiqo/components/Link';
import { extractServiceFromUrl } from '~/jobiqo/utils/links';
import { getFormattedPageLink } from '~/jobiqo/utils/routes';
import { HorizontalCardParagraph } from '~/jobiqo/types/types';
import EmbeddedVideo from '~/jobiqo/components/Landingpage/EmbeddedYoutube/components/EmbeddedVideo';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import {
  checkVideoConsent,
  mapVideoTypeToService,
} from '~/jobiqo/components/Landingpage/utils/landingPage';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { FormattedMessage } from 'react-intl';
import CMPServiceCheckbox from '~/jobiqo/components/CMPserviceCheckbox';
import AlignedCenter from '~/vcl/atoms/06-layout/01-alignment/01-align-center';
import NextLink from 'next/link';

interface HorizontalCardProps {
  /**
   * The horizontal card paragraph.
   */
  card: HorizontalCardParagraph;
}

export const HorizontalCard = ({ card }: HorizontalCardProps) => {
  const userContext: UserContextInterface = useContext(UserContext);
  let consented = true;
  let service = '';
  if (card.videoLink?.url) {
    service = extractServiceFromUrl(card.videoLink.url);
    consented = checkVideoConsent(service, userContext);
  }

  return (
    <HorizontalCardComponent
      title={card.title}
      {...(card.horizontalCardImage
        ? {
            media: (
              <Image
                src={card.horizontalCardImage.url}
                alt={card.horizontalCardImage.alt ?? card.title}
                title={card.horizontalCardImage.title ?? card.title}
                width={card.horizontalCardImage.width ?? 1000}
                height={card.horizontalCardImage.height ?? 1000}
              />
            ),
          }
        : card.videoLink && card.videoLink?.url
        ? {
            media: consented ? (
              <EmbeddedVideo link={card.videoLink} />
            ) : (
              <Spacing margin={0} padding={7}>
                <AlignedCenter>
                  <Text marginTop="0">
                    <FormattedMessage
                      id="video_teasers_row_consent"
                      defaultMessage="Consent to this service"
                    />
                  </Text>
                  <Spacing />
                  <CMPServiceCheckbox
                    serviceId={mapVideoTypeToService(service)}
                  />
                </AlignedCenter>
              </Spacing>
            ),
            hasVideo: true,
          }
        : null)}
      {...(card.horizontalCardContent
        ? {
            content: (
              <WYSIWYGText
                format="filtered_html"
                content={card.horizontalCardContent}
              />
            ),
          }
        : null)}
      {...(card.button && card.button.url
        ? {
            button: (
              <>
                <Link prefetch={false} asChild>
                  <LinkButton
                    linkElement={NextLink}
                    buttonStyle="primary"
                    {...getFormattedPageLink(card.button?.url)}
                  >
                    {card.button?.text}
                  </LinkButton>
                </Link>
              </>
            ),
          }
        : null)}
    />
  );
};

export default HorizontalCard;
